"use client"

import { ToastContainer as BaseToastContainer } from "react-toastify"
import styled, { css } from "styled-components"

import theme from "@/theme"

const { borders, colors, sizes, spaces, zIndex } = theme

export const ToastContainer = styled(BaseToastContainer)`
  ${({ $navigationWidth }) => {
    return css`
      & {
        --toastify-color-error: ${colors.danger.bg};
        --toastify-color-info: ${colors.secondary.bg};
        --toastify-color-success: ${colors.success.bg};
        --toastify-color-warning: ${colors.warning.bg};
        --toastify-icon-color-error: var(--toastify-color-error);
        --toastify-icon-color-info: var(--toastify-color-info);
        --toastify-icon-color-success: var(--toastify-color-success);
        --toastify-icon-color-warning: var(--toastify-color-warning);
      }

      @keyframes Toastify__trackProgress {
        0% {
          transform: scaleX(1);
        }
        100% {
          transform: scaleX(0);
        }
      }

      @keyframes Toastify__bounceInDown {
        from,
        60%,
        75%,
        90%,
        to {
          animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
        }
        0% {
          opacity: 0;
          transform: translate3d(0, -3000px, 0);
        }
        60% {
          opacity: 1;
          transform: translate3d(0, 25px, 0);
        }
        75% {
          transform: translate3d(0, -10px, 0);
        }
        90% {
          transform: translate3d(0, 5px, 0);
        }
        to {
          transform: none;
        }
      }

      @keyframes Toastify__bounceOutUp {
        20% {
          transform: translate3d(0, -10px, 0);
        }
        40%,
        45% {
          opacity: 1;
          transform: translate3d(0, 20px, 0);
        }
        to {
          opacity: 0;
          transform: translate3d(0, -2000px, 0);
        }
      }

      &.Toastify__toast-container {
        z-index: ${zIndex.toast};
        position: fixed;
        padding: ${spaces.extraSmall} 0;
        width: 25rem;
        font-size: 1.125rem;

        &--top-center {
          top: ${spaces.large};
          // 24px is the size of the grid gap between the navigation and main content
          left: calc(50% + ${($navigationWidth + 24) / 2}px);
          transform: translateX(-50%);
        }
      }

      .Toastify__toast-theme {
        &--dark {
          background: ${colors.brand.bg};
          color: ${colors.brand.fg};
        }

        &--light {
          background: ${colors.content.bg};
          color: ${colors.content.fg};
        }
      }

      .Toastify__toast {
        position: relative;
        min-height: ${sizes.mediumToLarge};
        margin-block-end: ${spaces.normal};
        padding: ${spaces.small} ${spaces.large};
        border-radius: ${borders.radius.standard};
        box-shadow:
          0 1px 10px 0 rgba(0, 0, 0, 0.1),
          0 2px 15px 0 rgba(0, 0, 0, 0.05);
        display: flex;
        justify-content: start;
        gap: ${spaces.small};
        max-height: ${sizes.huge};
        overflow: hidden;
        font-family: inherit;
        width: fit-content;

        &--close-on-click {
          cursor: pointer;
        }
      }

      .Toastify__toast-body {
        flex: 1 1 auto;
        display: flex;
        align-items: center;
        gap: ${spaces.small};

        & > div:last-child {
          word-break: break-word;
          flex: 1;
        }
      }

      .Toastify__toast-icon {
        margin-block-start: ${spaces.xxs};
        margin-inline-end: ${spaces.xtrasmall};
        width: ${sizes.small};
        flex-shrink: 0;
        display: flex;
        align-self: flex-start;
      }

      .Toastify--animate {
        animation-fill-mode: both;
        animation-duration: 0.7s;
      }

      .Toastify__progress-bar {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 5px;
        z-index: ${zIndex.toast};
        opacity: 0.7;
        transform-origin: left;

        &,
        &--animated {
          animation: Toastify__trackProgress linear 1 forwards;
        }

        &--controlled {
          transition: transform 0.2s;
        }
      }

      .Toastify__progress-bar-theme {
        &--light {
          background: ${colors.secondary.bg};
        }

        &--dark {
          background: ${colors.secondary.bg};
        }
      }

      .Toastify__close-button {
        color: ${colors.brand.fg};
        background: transparent;
        outline: none;
        border: none;
        padding: 0;
        cursor: pointer;
        opacity: 0.7;
        transition: 0.3s ease;
        align-self: flex-start;
        position: initial;

        &--light {
          color: ${colors.brand.bg};
          opacity: 0.3;
        }

        & > svg {
          fill: currentColor;
          height: ${sizes.small};
          width: ${sizes.small};
          margin-block-start: ${spaces.xxs};
        }

        &:hover,
        &:focus {
          opacity: 1;
        }
      }

      .Toastify__bounce-enter--top-center {
        animation-name: Toastify__bounceInDown;
      }

      .Toastify__bounce-exit--top-center {
        animation-name: Toastify__bounceOutUp;
      }
    `
  }}
`
